import type { TicketTypeTemplate } from '../types/ticketTypeTemplate';
import type { CustomerServiceStatus } from '@techgitter/contracts';
import {
  SERVICE_STATUS_ACTIVE,
  SERVICE_STATUS_ONBOARDING,
  SERVICE_STATUS_SUSPENDED,
} from '../components/tickets/Customer/customerServiceStatusOptions';

// Dataverse choice values for statuses that indicate a "visible" (usable) service
const VISIBLE_STATUS_VALUES = new Set([SERVICE_STATUS_ACTIVE, SERVICE_STATUS_ONBOARDING, SERVICE_STATUS_SUSPENDED]);

// Fallback: label-based matching for any unmapped status values
const VISIBLE_STATUS_LABELS = new Set(['Active', 'Onboarding', 'Suspended']);

// Templates with this serviceKey are always shown regardless of customer services
const ALWAYS_VISIBLE_SERVICE_KEY = 'other';

// Groups rendered in this order when the customer has both active (index = priority, lower = first)
const GROUP_PRIORITY: Record<string, number> = {
  'Google Workspace': 0,
  'M365': 1,
};

export function isVisibleStatus(status: CustomerServiceStatus['status']): boolean {
  return VISIBLE_STATUS_VALUES.has(status.value) || VISIBLE_STATUS_LABELS.has(status.label);
}

function activeServiceKeys(services: CustomerServiceStatus[]): Set<string> {
  return new Set(
    services
      .filter(s => isVisibleStatus(s.status))
      .map(s => s.serviceKey.toLowerCase())
  );
}

/**
 * Returns templates visible for the customer's active services.
 * Templates with serviceKey 'other' are always included.
 * If no customer is selected (empty services array), returns all templates.
 */
export function filterTemplatesByServices(
  templates: TicketTypeTemplate[],
  services: CustomerServiceStatus[],
  hasCustomer: boolean = false
): TicketTypeTemplate[] {
  if (!Array.isArray(templates)) return [];
  // No customer selected → show all templates
  if (!hasCustomer) return templates;
  // Customer selected → filter by their active service keys (may be empty)
  const keys = activeServiceKeys(services);
  return templates.filter(
    t => t.serviceKey.toLowerCase() === ALWAYS_VISIBLE_SERVICE_KEY || keys.has(t.serviceKey.toLowerCase())
  );
}

/**
 * Sorts templates so groups appear in consistent order.
 * When a customer has both Google Workspace and M365 active, Google comes first.
 * Within a group, original order is preserved.
 */
export function sortTemplateGroups(
  templates: TicketTypeTemplate[],
  services: CustomerServiceStatus[]
): TicketTypeTemplate[] {
  const keys = activeServiceKeys(services);
  const hasGoogle = keys.has('google');  // activeServiceKeys already lowercases
  const hasM365 = keys.has('m365');
  const applyPriority = hasGoogle && hasM365;

  if (!applyPriority) return templates;

  return [...templates].sort((a, b) => {
    const pa = GROUP_PRIORITY[a.group] ?? 99;
    const pb = GROUP_PRIORITY[b.group] ?? 99;
    return pa - pb;
  });
}

/**
 * Returns true when a selected template's service is not active for this customer.
 * Used to show the out-of-service warning.
 */
export function isTemplateServiceMismatch(
  template: TicketTypeTemplate,
  services: CustomerServiceStatus[]
): boolean {
  if (template.serviceKey.toLowerCase() === ALWAYS_VISIBLE_SERVICE_KEY) return false;
  if (services.length === 0) return false;
  return !activeServiceKeys(services).has(template.serviceKey.toLowerCase());
}
